import {useMemo} from "react";
import {useBusinessDetails} from "../../../../hooks/useBusinessDetails";
import {useBusinessEvents} from "../../../../hooks/useEvents";

export type OnboardingStepKey = "details" | "branding" | "social" | "banking" | "event";

export type OnboardingStep = {
    key: OnboardingStepKey;
    title: string;
    description: string;
    to: string;
    done: boolean;
};

export type OnboardingStatus = {
    steps: OnboardingStep[];
    completedCount: number;
    total: number;
    percent: number;
    isLoading: boolean;
    isComplete: boolean;
};

function isFilled(v: unknown): boolean {
    return typeof v === "string" && v.trim().length > 0;
}

export function useOnboardingStatus(businessId?: string): OnboardingStatus {
    const detailsQ = useBusinessDetails(businessId);
    const eventsQ = useBusinessEvents(businessId ?? "", {
        page: 0,
        size: 1,
        sort: {field: "startTime", dir: "DESC"},
        status: "PUBLISHED",
        queryOptions: {enabled: !!businessId, staleTime: 30_000} as any,
    });

    return useMemo<OnboardingStatus>(() => {
        const b = detailsQ.data ?? {};
        const publishedCount = eventsQ.data?.totalElements ?? 0;

        const detailsDone =
            isFilled(b.name) &&
            isFilled(b.email) &&
            isFilled(b.phoneNumber) &&
            isFilled(b.street) &&
            isFilled(b.city);

        const brandingDone = isFilled(b.imageUrl);

        const socialDone =
            isFilled(b.websiteUrl) ||
            isFilled(b.instagramUrl) ||
            isFilled(b.facebookUrl) ||
            isFilled(b.xUrl) ||
            isFilled(b.tiktokUrl);

        const bankingDone =
            isFilled(b.bankName) &&
            isFilled(b.accountNumber) &&
            isFilled(b.accountHolder) &&
            isFilled(b.branchCode);

        const eventDone = publishedCount >= 1;

        const steps: OnboardingStep[] = [
            {
                key: "details",
                title: "Add your business details",
                description: "Name, contact info and address — shown on tickets and receipts.",
                to: "/business/profile/details",
                done: detailsDone,
            },
            {
                key: "branding",
                title: "Upload your branding",
                description: "Add a logo so your events look polished across the platform.",
                to: "/business/profile/branding",
                done: brandingDone,
            },
            {
                key: "social",
                title: "Connect social media",
                description: "Link at least one social or website so customers can follow you.",
                to: "/business/profile/social-media",
                done: socialDone,
            },
            {
                key: "banking",
                title: "Add banking details",
                description: "Required so we can pay out your ticket sales.",
                to: "/business/profile/banking",
                done: bankingDone,
            },
            {
                key: "event",
                title: "Create and publish your first event",
                description: "Set up tickets, branding and publish — start selling in minutes.",
                to: "/business/events/new",
                done: eventDone,
            },
        ];

        const completedCount = steps.filter((s) => s.done).length;
        const total = steps.length;
        const percent = total > 0 ? Math.round((completedCount / total) * 100) : 0;
        const isLoading = (!!businessId && detailsQ.isLoading) || (!!businessId && eventsQ.isLoading);

        return {
            steps,
            completedCount,
            total,
            percent,
            isLoading,
            isComplete: completedCount === total,
        };
    }, [detailsQ.data, detailsQ.isLoading, eventsQ.data, eventsQ.isLoading, businessId]);
}

export function welcomeDismissedKey(businessId: string) {
    return `welcome_dismissed_${businessId}`;
}

export function welcomeSeenKey(businessId: string) {
    return `welcome_seen_${businessId}`;
}

export function isWelcomeDismissed(businessId?: string | null): boolean {
    if (!businessId) return false;
    try {
        return localStorage.getItem(welcomeDismissedKey(businessId)) === "1";
    } catch {
        return false;
    }
}

export function setWelcomeDismissed(businessId: string, dismissed: boolean) {
    try {
        if (dismissed) localStorage.setItem(welcomeDismissedKey(businessId), "1");
        else localStorage.removeItem(welcomeDismissedKey(businessId));
    } catch {
        /* ignore */
    }
}

export function wasWelcomeSeen(businessId?: string | null): boolean {
    if (!businessId) return false;
    try {
        return localStorage.getItem(welcomeSeenKey(businessId)) === "1";
    } catch {
        return false;
    }
}

export function markWelcomeSeen(businessId: string) {
    try {
        localStorage.setItem(welcomeSeenKey(businessId), "1");
    } catch {
        /* ignore */
    }
}
